import * as React from 'react'
import { Control, Node, Socket } from 'rete-react-render-plugin'
import $ from 'jquery'
import { agentHighlightClassForNode, classForNode, subscribeNodeFocus } from './node_focus_store'
import { parseUnitTitle, UnitBadge } from './unit_badge'

/** Rete mounts custom controls into `el`; avoids `Control` wrapper so we don’t inherit `title={control.key}` (extra tooltips). */
function ReteControlMount({
  control,
  className,
  bindControl,
}: {
  control: any
  className: string
  bindControl: (el: HTMLElement | null, ctrl: any) => void
}) {
  const cb = (el: HTMLElement | null) => {
    if (el) bindControl(el, control)
  }
  return <div className={className} ref={cb} />
}

export class NodeComponent extends Node {
  private inputDebounceTimer: any
  private unsubscribeFocus: (() => void) | null = null
  /** Root <div> of the rendered node; used to scope tooltip refresh to
   *  only this node's titled descendants instead of the entire document. */
  private rootEl: HTMLDivElement | null = null
  props: any // TODO
  state: any // TODO

  constructor(props: any) {
    super(props)
    // Rete's Node base uses getDerivedStateFromProps; initialize for consistent shape.
    this.state = {
      outputs: [],
      controls: [],
      inputs: [],
      selected: ""
    }
  }

  componentDidMount() {
    // Re-render whenever the editor's focus / connected-chain selection
    // changes so the highlight class flows into our `className` (and thus
    // can't be overwritten by the next React reconciliation, unlike direct
    // DOM mutation).
    this.unsubscribeFocus = subscribeNodeFocus(() => {
      ;(this as any).setState({})
    })
    this.refreshTooltips()
    // `Node` from rete-react-render-plugin doesn't define componentDidMount,
    // so no `super` call is required.
  }

  componentDidUpdate() {
    // Controls / icons with `title` attributes can appear conditionally
    // between renders (e.g. error state, isRunning, custom name breadcrumb)
    // so we re-init tooltips on update — but only for *this* node, not the
    // entire document.
    this.refreshTooltips()
  }

  componentWillUnmount() {
    if (this.unsubscribeFocus) {
      this.unsubscribeFocus()
      this.unsubscribeFocus = null
    }
    if (this.rootEl) {
      try {
        $(this.rootEl).find('[title]').tooltip('dispose')
      } catch (_err) {
        // Swallow: jQuery / Bootstrap tooltip plugin may not be loaded, or
        // the element may already be detached. Either way nothing useful to
        // do here.
      }
    }
  }

  /**
   * Initialise Bootstrap tooltips for `[title]` elements **inside this node only**.
   *
   * The previous implementation ran `$('[title]').tooltip('dispose').tooltip()`
   * inside `render()` (wrapped in a `setTimeout`), which traversed the entire
   * document on every render of every node — O(N²) DOM work for large models
   * and the primary cause of the noticeable lag where SVG connection paths
   * lagged a couple of seconds behind the node boxes on project open
   * (the main thread was busy thrashing jQuery instead of laying out SVG).
   * Scoping the query to `this.rootEl` makes the work per-node small and the
   * total cost across the graph O(N).
   */
  private refreshTooltips() {
    const root = this.rootEl
    if (!root) return
    try {
      $(root).find('[title]').tooltip('dispose').tooltip()
    } catch (_err) {
      // See note in componentWillUnmount.
    }
  }


  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    const { node, editor } = this.props

    node.data.name = e.target.value

    const t: any = this // TODO: a little hacky to appease typescript, I'd prefer something a little cleaner 
    t.setState({})

    clearTimeout(this.inputDebounceTimer)

    this.inputDebounceTimer = setTimeout(() => {
      editor.trigger("process")
    }, 1000)
  }

  render() {
    const { node, editor, bindSocket, bindControl } = this.props
    const { outputs, controls, inputs, selected } = this.state

    const top_controls = controls.filter((control: any) => control.placement === "top")
    const select_controls = controls.filter((control: any) => control.type && (control.type === "SelectControl" || control.type === "CheckboxSelectControl") && control.placement !== "after-above-io")
    const after_above_io_select_controls = controls.filter((control: any) => control.type && (control.type === "SelectControl" || control.type === "CheckboxSelectControl") && control.placement === "after-above-io")
    const date_controls = controls.filter((control: any) => control.type && control.type === "DateControl")
    const preset_buttons_controls = controls.filter((control: any) => control.type === "PresetButtonsControl")
    const above_io_controls = controls.filter((control: any) => control.type === "AboveIoControl")
    const custom_query_controls = controls.filter((control: any) => control.type && control.type === "OSMCustomQueryControl")
    const payoff_matrix_controls = controls.filter((control: any) => control.type === "PayoffMatrixControl")
    const table_editor_controls = controls.filter((control: any) => control.type === "TableEditorControl")
    const header_boolean_controls = controls.filter((control: any) => control.type === "BooleanControl")
    const expose_toggle_controls = controls.filter((control: any) => control.type === "ExposeToggleControl")
    const non_select_controls = controls.filter((control: any) =>
      control.type !== "SelectControl" &&
      control.type !== "CheckboxSelectControl" &&
      control.type !== "DateControl" &&
      control.type !== "PresetButtonsControl" &&
      control.type !== "AboveIoControl" &&
      control.type !== "OSMCustomQueryControl" &&
      control.type !== "PayoffMatrixControl" &&
      control.type !== "TableEditorControl" &&
      control.type !== "BooleanControl" &&
      control.type !== "ExposeToggleControl" &&
      control.placement !== "top")

    // Breadcrumb: "category/Component Name".
    //   - No custom name set → shown as the input placeholder (current position).
    //   - Custom name set    → shown small above the input, name goes in the
    //     input where the placeholder used to be.
    // We look the component up via `editor.getComponent` so we can read
    // `category` / `contextMenuName` from the BaseComponent without having to
    // pass extra props through Rete's render pipeline.
    const registered = (editor?.getComponent?.(node.name) as any) ?? null
    const category: string | undefined = registered?.category
    const displayComponentName: string =
      (typeof registered?.contextMenuName === "string" && registered.contextMenuName.trim()) ||
      node.name
    const breadcrumb = category
      ? `${category.toLowerCase()}/${displayComponentName}`
      : displayComponentName
    const customName: string =
      typeof node.data?.name === "string" ? node.data.name : ""
    const hasCustomName = customName.trim().length > 0

    const isCategoricalToNumeric = node.name === "Categorical to numeric dataset"
    const primaryInputs = isCategoricalToNumeric
      ? inputs.filter((input: any) => !String(input.key).startsWith("catVal_"))
      : inputs
    const labelInputs = isCategoricalToNumeric
      ? inputs.filter((input: any) => String(input.key).startsWith("catVal_"))
      : []

    const renderInput = (input: any) => (
      <div className="input" key={`${String(input.key)}:${String(input.name ?? "")}`}>
        <div title={input.socket?.name === "Number or Numeric dataset" ? input.name : undefined}>
          <Socket type="input" socket={input.socket} io={input} innerRef={bindSocket} />
        </div>
        {!input.showControl() && input.control?.type !== "TextNumericControl" && <div className="input-title">{input.name}</div>}
        {(input.showControl() || input.control?.type === "TextNumericControl") && (
          <Control className="input-control" control={input.control} innerRef={bindControl} />
        )}
      </div>
    )

    const focusClass = classForNode(node.id)
    const agentRefClass = agentHighlightClassForNode(node.id)
    return (
      <div
        ref={(el: HTMLDivElement | null) => { this.rootEl = el }}
        className={`node ${selected} ${focusClass} ${agentRefClass}`.trim().replace(/\s+/g, " ")}
        data-node-id={node.id}
        style={{
          boxShadow:  "0px 0px 8px rgba(0, 0, 0, 0.25)",
          background: "rgba(0, 0, 0, 0.5)",
          color: "white",
          borderRadius: "4px",
          border:  node.meta.errorMessage ? "solid 2px rgba(210, 0, 0, .71)" : "solid 3px transparent",
          cursor: "pointer",
          minWidth: "250px",
          height: "auto",
          boxSizing: "content-box",
          position: "relative",
          userSelect: "none",
        }}
      >
        <div style={{ padding: "calc(.5rem - 3px)" }} className="d-flex align-items-center">
          {/* Single grey "box" containing both the breadcrumb (when a custom
              name is set) and the editable name input. Wrapping the input in
              a dark container with the input set to bg-transparent merges
              them visually so they look like one field rather than two. */}
          <div
            className="bg-dark text-light"
            style={{
              width: "0px",
              flexGrow: 1,
              borderRadius: "0.25rem",
              overflow: "hidden",
            }}
          >
            {hasCustomName && (
              <div
                className="node-breadcrumb"
                style={{
                  padding: "4px 12px 0",
                  marginBottom: "-2px",
                  fontSize: "0.65rem",
                  lineHeight: 1.1,
                  color: "rgba(255, 255, 255, 0.55)",
                  letterSpacing: "0.02em",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={breadcrumb}
              >
                {breadcrumb}
              </div>
            )}
            <input
              type="text"
              className="form-control bg-transparent text-light border-0 node-name-input"
              style={{
                boxShadow: "none",
                width: "100%",
                fontSize: "0.85rem",
                lineHeight: 1.35,
                paddingTop: hasCustomName ? 0 : undefined,
                // Parent `.node` uses cursor:pointer + userSelect:none for
                // canvas dragging; override so the name field always shows a
                // text caret (including when empty / placeholder-only).
                cursor: "text",
                userSelect: "text",
              }}
              placeholder={hasCustomName ? displayComponentName : breadcrumb}
              value={customName}
              onChange={this.handleInputChange}
              onPointerDown={e => e.stopPropagation()}
              onDoubleClick={e => e.stopPropagation()}
            />
          </div>
          {
            node.meta.toolTip &&
            <i
              className="fas fa-question-circle text-secondary"
              style={{ marginLeft: -26, paddingRight: 10 }}
              onClick={() => { node.meta.toolTipLink ? window.open(node.meta.toolTipLink) : null }}
              title={node.meta.toolTip}
            />
          }
          {
            node.meta.isRunning &&
            <i 
              className="fas fa-spinner fa-spin text-secondary ml-2 text-success"
            />
          }
          {
            node.meta.errorMessage &&
            <i
              className="fas fa-exclamation-triangle ml-2 mb-1 text-danger"
              title={node.meta.errorMessage}
            />
          }
        </div>

        {top_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}

        {header_boolean_controls.map(control => (
          <div key={control.key} className="px-2 pb-1" style={{ color: "white" }}>
            <Control className="control" control={control} innerRef={bindControl} />
          </div>
        ))}

        {select_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}
        {preset_buttons_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}
        {date_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}
        {above_io_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}
        {after_above_io_select_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}
        {custom_query_controls.map(control => (
          <Control className="control" key={control.key} control={control} innerRef={bindControl} />
        ))}

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {primaryInputs.map(renderInput)}
          </div>
          <div>
            {outputs.map((output) => {
              const { name, units } = parseUnitTitle(output.name)
              return (
                <div className="output" key={output.key}>
                  <div className="output-title">
                    {name}
                    <UnitBadge units={units} />
                  </div>
                  <Socket type="output" socket={output.socket} io={output} innerRef={bindSocket} />
                </div>
              )
            })}
          </div>
        </div>
        {expose_toggle_controls.map(control => (
          <Control
            className="control expose-toggle-control-wrapper expose-toggle-control-wrapper--below-io"
            key={control.key}
            control={control}
            innerRef={bindControl}
          />
        ))}
        {labelInputs.length > 0 && (
          <div className="cat-to-numeric-label-inputs">
            {labelInputs.map(renderInput)}
          </div>
        )}
        {payoff_matrix_controls.map(control => (
          <ReteControlMount
            key={control.key}
            control={control}
            className="control payoff-matrix-control-wrapper payoff-matrix-control-wrapper--below-io"
            bindControl={bindControl}
          />
        ))}
        {table_editor_controls.map(control => (
          <ReteControlMount
            key={control.key}
            control={control}
            className="control table-editor-control-wrapper table-editor-control-wrapper--below-io"
            bindControl={bindControl}
          />
        ))}
        {non_select_controls.map(control => (
          <Control
            className={
              control.type === "NumericConstantRowControl"
                ? "control numeric-constant-row-control"
                : "control"
            }
            key={control.key}
            control={control}
            innerRef={bindControl}
          />
        ))}
      </div>
    )
  }
}
